const DEFAULT_LOCALE = "en";

/** Normalizes a BCP 47 tag such as Teams' lower-case `fr-ca` to `fr-CA`. */
function canonicalize(tag: string): string | null {
  try {
    return Intl.getCanonicalLocales(tag)[0] ?? null;
  } catch {
    return null;
  }
}

function getLanguage(tag: string): string {
  return tag.split("-")[0].toLowerCase();
}

/**
 * Picks the best supported locale for the given candidates, in order of
 * preference. A candidate matches a supported locale exactly, or by language
 * when no regional variant matches (e.g. `fr-FR` resolves to `fr-CA` when
 * `fr-CA` is the only French locale available). Falls back to English.
 */
export default function resolveLocale<T extends string>(
  candidates: readonly (string | null | undefined)[],
  supported: readonly T[],
): T {
  for (const candidate of candidates) {
    const tag = candidate && canonicalize(candidate);
    if (!tag) {
      continue;
    }

    const exact = supported.find((locale) => locale === tag);
    if (exact) {
      return exact;
    }

    const language = getLanguage(tag);
    const byLanguage = supported.find(
      (locale) => getLanguage(locale) === language,
    );
    if (byLanguage) {
      return byLanguage;
    }
  }

  return supported.find((locale) => locale === DEFAULT_LOCALE) ?? supported[0];
}
